import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { ShieldCheck, Target, MonitorPlay, BarChart3, Lightbulb, FastForward, CheckCircle2 } from 'lucide-react';
interface SlideDifferentiationProps {
  onContinue: () => void;
}
export function SlideDifferentiation({
  onContinue
}: SlideDifferentiationProps) {
  const [stage, setStage] = useState(0);
  const items = [{
    icon: <ShieldCheck className="w-5 h-5" data-id="element-216" />,
    title: 'Deep Vertical Expertise & Master Agreements',
    desc: 'Navigating regulated environments where generalist AI fails.'
  }, {
    icon: <Target className="w-5 h-5" data-id="element-217" />,
    title: 'One-to-One',
    desc: "Precision delivery vs agencies' broad-stroke campaigns."
  }, {
    icon: <MonitorPlay className="w-5 h-5" data-id="element-218" />,
    title: 'Interactive Engagement Platform',
    desc: 'Proprietary engagement layer driving measurable outcomes.'
  }, {
    icon: <BarChart3 className="w-5 h-5" data-id="element-219" />,
    title: 'Compounding Flywheel',
    desc: 'Behavioral data refines the model weekly, increasing the gap.'
  }];
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 300);
    const t2 = setTimeout(() => setStage(2), 800);
    const t3 = setTimeout(() => setStage(3), 1300);
    const timers = items.map((_, idx) => setTimeout(() => setStage(4 + idx), 1800 + idx * 300));
    const t4 = setTimeout(() => setStage(4 + items.length), 1800 + items.length * 300 + 300);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      timers.forEach(clearTimeout);
      clearTimeout(t4);
    };
  }, []);
  // SVG curves: Agency (red) declines, Snakker (teal) rises, crossing in middle
  const agencyCurve = 'M 30 60 C 120 60 180 65 240 130 C 300 200 360 220 470 230';
  const snakkerCurve = 'M 30 230 C 120 225 180 210 240 130 C 300 60 360 60 470 60';
  return <div className="h-full w-full flex flex-col px-8 py-6 md:px-16 md:py-8 max-w-7xl mx-auto overflow-y-auto" data-id="element-220">
      {/* Heading */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: stage >= 1 ? 1 : 0,
      y: stage >= 1 ? 0 : 20
    }} data-id="element-221">
        <h2 className="text-3xl md:text-5xl font-sans font-bold text-white tracking-tight leading-tight" data-id="element-222">
          Unique Snakker features
        </h2>
        <p className="text-teal-400 text-base md:text-lg font-medium mt-1" data-id="element-223">
          Differentiation & Vertical Leadership
        </p>
      </motion.div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center mt-8" data-id="element-224">
        {/* LEFT — items list */}
        <div className="space-y-5" data-id="element-225">
          {items.map((item, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: stage >= 4 + idx ? 1 : 0,
          x: stage >= 4 + idx ? 0 : -20
        }} transition={{
          duration: 0.5
        }} className="flex items-start gap-4" data-id="element-226">
              <div className="w-10 h-10 rounded-xl bg-teal-400/10 border border-teal-400/30 flex items-center justify-center text-teal-400 flex-shrink-0" data-id="element-227">
                {item.icon}
              </div>
              <div data-id="element-228">
                <h3 className="text-base md:text-lg font-bold text-white" data-id="element-229">
                  {item.title}
                </h3>
                <p className="text-white/60 text-sm mt-0.5 leading-snug" data-id="element-230">
                  {item.desc}
                </p>
              </div>
            </motion.div>)}
        </div>

        {/* RIGHT — Expertise Spectrum chart */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: stage >= 3 ? 1 : 0,
        scale: stage >= 3 ? 1 : 0.95
      }} transition={{
        duration: 0.6
      }} className="bg-navy-900/60 border border-navy-600 rounded-2xl p-5" data-id="element-231">
          <p className="text-white/40 text-[10px] uppercase tracking-widest font-semibold mb-3" data-id="element-232">
            Expertise Spectrum
          </p>

          {/* Legend */}
          <div className="flex items-center justify-between mb-2 text-[10px]" data-id="element-233">
            <div className="flex items-center gap-2" data-id="element-234">
              <div className="w-4 h-0.5 bg-red-400" data-id="element-235" />
              <span className="text-red-400 font-semibold" data-id="element-236">
                Agency expertise
              </span>
            </div>
            <div className="flex items-center gap-2" data-id="element-237">
              <span className="text-teal-400 font-semibold" data-id="element-238">
                Snakker expertise
              </span>
              <div className="w-4 h-0.5 bg-teal-400" data-id="element-239" />
            </div>
          </div>

          <div className="relative aspect-[2/1]" data-id="element-240">
            <svg viewBox="0 0 500 280" className="w-full h-full" data-id="element-241">
              <defs data-id="element-242">
                <linearGradient id="agencyGrad" x1="0" y1="0" x2="0" y2="1" data-id="element-243">
                  <stop offset="0%" stopColor="#F87171" stopOpacity="0.2" data-id="element-244" />
                  <stop offset="100%" stopColor="#F87171" stopOpacity="0" data-id="element-245" />
                </linearGradient>
                <linearGradient id="snakkerGrad" x1="0" y1="0" x2="0" y2="1" data-id="element-246">
                  <stop offset="0%" stopColor="#7c3aed" stopOpacity="0.2" data-id="element-247" />
                  <stop offset="100%" stopColor="#7c3aed" stopOpacity="0" data-id="element-248" />
                </linearGradient>
              </defs>

              {/* Grid lines */}
              {[60, 110, 160, 210].map(y => <line key={y} x1="30" y1={y} x2="470" y2={y} stroke="#cbd5e1" strokeOpacity="0.5" strokeWidth="0.5" data-id="element-249" />)}
              {[140, 250, 360].map(x => <line key={x} x1={x} y1="60" x2={x} y2="230" stroke="#cbd5e1" strokeOpacity="0.5" strokeWidth="0.5" data-id="element-250" />)}

              {/* Area fills */}
              <motion.path d={`${agencyCurve} L 470 230 L 30 230 Z`} fill="url(#agencyGrad)" initial={{
              opacity: 0
            }} animate={{
              opacity: stage >= 3 ? 1 : 0
            }} transition={{
              duration: 0.8,
              delay: 0.6
            }} data-id="element-251" />
              <motion.path d={`${snakkerCurve} L 470 230 L 30 230 Z`} fill="url(#snakkerGrad)" initial={{
              opacity: 0
            }} animate={{
              opacity: stage >= 3 ? 1 : 0
            }} transition={{
              duration: 0.8,
              delay: 0.6
            }} data-id="element-252" />

              {/* Curves */}
              <motion.path d={agencyCurve} fill="none" stroke="#F87171" strokeWidth="2.5" initial={{
              pathLength: 0
            }} animate={{
              pathLength: stage >= 3 ? 1 : 0
            }} transition={{
              duration: 1.4,
              ease: 'easeInOut'
            }} data-id="element-253" />
              <motion.path d={snakkerCurve} fill="none" stroke="#7c3aed" strokeWidth="2.5" initial={{
              pathLength: 0
            }} animate={{
              pathLength: stage >= 3 ? 1 : 0
            }} transition={{
              duration: 1.4,
              ease: 'easeInOut'
            }} data-id="element-254" />

              {/* Crossing point */}
              <motion.circle cx="240" cy="130" r="5" fill="#ffffff" stroke="#7c3aed" strokeWidth="2" initial={{
              opacity: 0,
              scale: 0
            }} animate={{
              opacity: stage >= 3 ? 1 : 0,
              scale: stage >= 3 ? 1 : 0
            }} transition={{
              delay: 1.4
            }} data-id="element-255" />

              {/* Axis labels */}
              <text x="30" y="265" className="fill-slate-500 text-[11px] font-semibold" data-id="element-256">
                One-to-many
              </text>
              <text x="470" y="265" textAnchor="end" className="fill-teal-400 text-[11px] font-bold" data-id="element-257">
                One-to-one
              </text>
            </svg>
          </div>
        </motion.div>
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= 4 + items.length ? 1 : 0
    }} className="mt-6 flex justify-end" data-id="element-258">
        <Button onClick={onContinue} data-id="element-259">New Category</Button>
      </motion.div>
    </div>;
}